// MyTripsPage.js

import React, {useEffect, useState, useRef, useMemo} from 'react';
import SearchBar from '../components/SearchBar';
import TripPreview from '../components/TripPreview';
import TripStatusToggle from '../components/TripStatusToggle';
import InProcessPopup from '../components/InProcessPopup';
import {collection, doc, getDoc} from "firebase/firestore";
import {db} from "../firebase/firebase";
import './MyTripsPage.css';
import {useCollection} from 'react-firebase-hooks/firestore';
import {useUser} from '../contexts/UserProvider';
import SingleJoinTripCard from '../components/SingleJoinTripCard';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const MyTripsPage = () => {
    const {profile} = useUser();
    const [statusFilter, setStatusFilter] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [finalTrips, setFinalTrips] = useState([]);
    const [singleTripSnapshot] = useCollection(collection(db, "SingleUserTrips"));
    const [singleTrips, setSingleTrips] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingTrips, setLoadingTrips] = useState(true);
    const [snapshot] = useCollection(collection(db, "tripEvents"));
    const scrollRef = useRef(null);
    const [showSoloArrows, setShowSoloArrows] = useState(false);
    const soloScrollRef = useRef(null);
    const fetchTripsRef = useRef();

    const handleSearch = (term) => setSearchTerm(term.toLowerCase());

    const handleStatusChange = (event, newStatus) => setStatusFilter(newStatus);

    const handleProfileClick = (profile) => {
        console.log(profile)
        if (profile.status === 'in-process') {
            setSelectedProfile(profile);
            setIsPopupOpen(true);
            return true;
        }
    };

    const scrollByAmount = (amount) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({left: amount, behavior: "smooth"});
        }
    };

    const scrollSoloByAmount = (amount) => {
        if (soloScrollRef.current) {
            soloScrollRef.current.scrollBy({left: amount, behavior: "smooth"});
        }
    };

    useEffect(() => {
        if (!singleTripSnapshot || !profile) return;

        const filtered = singleTripSnapshot.docs
            .map(doc => ({id: doc.id, ...doc.data()}))
            .filter(trip =>
                trip.eventOwner === profile.id ||
                (trip.participants || []).includes(profile.id)
            );

        setSingleTrips(filtered);
    }, [singleTripSnapshot, profile]);

    useEffect(() => {
        if (!profile) return;

        const fetchTrips = async () => {
            setLoadingTrips(true);
            try {
                if (!snapshot) return;

                const now = new Date();

                const enrichedTrips = await Promise.all(snapshot.docs.map(async (docSnap) => {
                    const event = docSnap.data();
                    const templateRef = doc(db, "tripTemplates", event.templateId);
                    const templateSnap = await getDoc(templateRef);
                    if (!templateSnap.exists()) return null;

                    const template = templateSnap.data();
                    const timestampDate = event.timestamp?.toDate?.() ?? new Date();
                    const dynamicStatus = timestampDate < now ? 'past' : 'planned';

                    const { id: _eventId, ...eventWithoutId } = event;
                    const { id: _templateId, ...templateWithoutId } = template;

                    return {
                        id: docSnap.id,
                        ...templateWithoutId,
                        ...eventWithoutId,
                        status: dynamicStatus,
                        tripDate: timestampDate,
                    };
                }));
                console.log(enrichedTrips)
                const filteredFinalTrips = enrichedTrips
                    .filter(trip => trip !== null)
                    .filter(trip => trip.participants.includes(profile.id))
                    .filter(trip => {
                        if (!statusFilter) return true;
                        const isPast = trip.tripDate < now;
                        if (statusFilter === 'past') return isPast;
                        if (statusFilter === 'planned') return !isPast;
                        return false;
                    });
                console.log(filteredFinalTrips)
                filteredFinalTrips.sort((a, b) => b.tripDate - a.tripDate);
                setFinalTrips(filteredFinalTrips);
            } catch (error) {
                console.error("Error fetching trips from Firestore:", error);
            } finally {
                setLoadingTrips(false);
            }
        };

        fetchTripsRef.current = fetchTrips;
        fetchTrips();
    }, [statusFilter, profile, snapshot]);

    const visibleSingleTrips = singleTrips.filter(trip => {
        // Exclude trips where the latest date is in the past
        if (trip.dates && trip.dates.length > 0) {
            // Convert all dates to JS Date objects
            const dateObjs = trip.dates.map(ts => ts?.toDate?.() || ts).filter(Boolean);
            // Find the latest date
            const latestDate = dateObjs.reduce((max, d) => d > max ? d : max, new Date(0));
            // If the latest date is before now, exclude this trip
            if (latestDate < new Date()) return false;
        }
        if (!searchTerm) return true;
        const dateStrings = (trip.dates || []).map(ts => {
            const d = ts?.toDate?.();
            if (!d) return '';
            const day = String(d.getDate());
            const short = `${day.padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
            const full = d.toLocaleDateString('he-IL');
            const year = String(d.getFullYear());
            const time = d.toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit', hour12: false});
            return [day, short, full, year, time].join(' ');
        }).join(' ');

        const allFields = `
            ${trip.title}
            ${trip.style}
            ${trip.forWhom}
            ${trip.difficulty}
            ${trip.duration}
            ${trip.location}
            ${trip.description}
            ${trip.gender}
            ${trip.tripType}
            ${trip.region}
            ${trip.groupSize}
            ${dateStrings}
        `.toLowerCase();

        return allFields.includes(searchTerm);
    });

    const visibleFinalTrips = finalTrips.filter(trip => {
        if (!searchTerm) return true;

        const d = trip.tripDate ? new Date(trip.tripDate) : null;
        const dateStr = d?.toLocaleDateString('he-IL') || '';
        const shortDate = d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}` : '';
        const year = d ? String(d.getFullYear()) : '';
        const timeStr = d?.toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit', hour12: false}) || '';

        const fields = `
            ${trip.title}
            ${trip.style}
            ${trip.forWhom}
            ${trip.difficulty}
            ${trip.duration}
            ${trip.location}
            ${trip.description}
            ${dateStr} ${shortDate} ${year} ${timeStr}
        `.toLowerCase();

        return fields.includes(searchTerm);
    });

    useMemo(() => {
        console.log(visibleSingleTrips);
        console.log(profile.id)
    }, [visibleSingleTrips]);

    useEffect(() => {
        const checkOverflow = () => {
            const el = soloScrollRef.current;
            console.log('Checking overflow for solo trips...');
            if (el) {
                console.log('Solo scroll check:', {
                    scrollWidth: el.scrollWidth,
                    clientWidth: el.clientWidth,
                    hasOverflow: el.scrollWidth > el.clientWidth,
                    visibleTrips: visibleSingleTrips.length
                });
                setShowSoloArrows(el.scrollWidth > el.clientWidth);
            } else {
                console.log('soloScrollRef.current is null');
            }
        };
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [visibleSingleTrips]);

    return (
        <div className="my-trips-page">
            <SearchBar onSearch={handleSearch}/>
            <TripStatusToggle statusFilter={statusFilter} onStatusChange={handleStatusChange}/>

            <div className="content">
                <div className="trips-list" dir="rtl">
                    {(!statusFilter || statusFilter === 'in-process') && (
                        <div className="scroll-wrapper">
                            <div className="horizontal-scroll-container">
                                {showSoloArrows && (
                                    <button className="scroll-arrow left" onClick={() => scrollSoloByAmount(-300)}>
                                        <ChevronLeftIcon/>
                                    </button>
                                )}
                                <div className="horizontal-scroll" ref={soloScrollRef}>
                                    {visibleSingleTrips.map((singleTrip, index) => (
                                        <SingleJoinTripCard
                                            key={index}
                                            profile={singleTrip}
                                            onClick={() => {
                                                return handleProfileClick(singleTrip)
                                            }}
                                        />
                                    ))}
                                </div>
                                {showSoloArrows && (
                                    <button className="scroll-arrow right" onClick={() => scrollSoloByAmount(300)}>
                                        <ChevronRightIcon/>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {(statusFilter === null || statusFilter !== 'in-process') && !loadingTrips && (
                        <>
                            {visibleFinalTrips.length === 0 ? (
                                <div>לא קיימים טיולים שהצטרפת אליהם</div>
                            ) : (
                                visibleFinalTrips.map(trip => (
                                    <TripPreview 
                                        trip={trip} 
                                        key={trip.id}
                                        isJoining={false}
                                        onAfterCancel={() => fetchTripsRef.current && fetchTripsRef.current()} 
                                    />
                                ))
                            )}
                        </>
                    )}
                </div>
            </div>

            <InProcessPopup
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                profile={selectedProfile}
            />
        </div>
    );
};

export default MyTripsPage;
